import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  NumberInput,
} from "@heroui/react";
import type { Table as TanstackTable } from "@tanstack/react-table";
import { ChevronDown, ChevronUp, Pin } from "lucide-react";

import { orderedLeafColumns } from "./columnOrder";

/**
 * 表示列の設定ダイアログ。
 *
 * 表示/非表示・並び順・列幅をまとめて操作する。設定は画面ごとに localStorage へ
 * 永続化される（[[useTableSettings]]）。並べ替えはドラッグではなく上下ボタンに
 * している（追加の DnD ライブラリを持ち込まずに済み、キーボードでも操作できる）。
 *
 * `meta.mandatory` が true の列は常に表示し、非表示にできない
 * （見積番号・操作列のように、消えると行を識別できなくなる列に使う）。
 * `meta.pinned` の列は左に固定され、並べ替えの対象外。
 *
 * 開閉は呼び出し側が持つ。ツールバーのボタンから開く画面と、ケバブメニューから
 * 開く画面があるため（[[ColumnSettingsButton]] はボタン付きの薄い包み）。
 */
export default function ColumnSettingsDialog<T>({
  isOpen,
  onClose,
  table,
  onReset,
}: {
  isOpen: boolean;
  onClose: () => void;
  table: TanstackTable<T>;
  onReset: () => void;
}) {
  // 非表示列も含めて表示順に並べる（getAllLeafColumns は columnOrder を
  // 反映しないため、専用のヘルパを使う）。
  const columns = orderedLeafColumns(table);
  // 固定列は常に先頭。ここより上へは動かせない。
  const pinnedCount = columns.findIndex((c) => !c.columnDef.meta?.pinned);

  const move = (columnId: string, direction: -1 | 1) => {
    const order = columns.map((c) => c.id);
    const from = order.indexOf(columnId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;
    [order[from], order[to]] = [order[to], order[from]];
    table.setColumnOrder(order);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>表示列の設定</span>
          <span className="text-small font-normal text-default-500">
            表示する列・並び順・列幅を変更できます。設定はこのブラウザに保存されます。
          </span>
        </ModalHeader>

        <ModalBody>
          <div className="flex flex-col gap-1">
            {columns.map((column, index) => {
              const mandatory = column.columnDef.meta?.mandatory === true;
              const pinned = column.columnDef.meta?.pinned === true;
              const label = column.columnDef.meta?.label ?? column.id;
              return (
                <div
                  key={column.id}
                  className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-default-100"
                >
                  <Checkbox
                    isSelected={column.getIsVisible()}
                    isDisabled={mandatory}
                    onValueChange={(v) => column.toggleVisibility(v)}
                    classNames={{ label: "text-sm" }}
                  >
                    {label}
                    {mandatory && (
                      <span className="ml-2 text-tiny text-default-400">
                        （常に表示）
                      </span>
                    )}
                    {pinned && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-tiny text-default-400">
                        <Pin size={11} />
                        左に固定
                      </span>
                    )}
                  </Checkbox>

                  <div className="ml-auto flex items-center gap-2">
                    <NumberInput
                      aria-label={`${label} の列幅`}
                      size="sm"
                      className="w-28"
                      minValue={60}
                      maxValue={600}
                      step={10}
                      value={column.getSize()}
                      onValueChange={(v) =>
                        table.setColumnSizing((prev) => ({
                          ...prev,
                          [column.id]: v,
                        }))
                      }
                      endContent={
                        <span className="text-tiny text-default-400">px</span>
                      }
                    />
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label={`${label} を上へ`}
                      isDisabled={pinned || index <= pinnedCount}
                      onPress={() => move(column.id, -1)}
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label={`${label} を下へ`}
                      isDisabled={pinned || index === columns.length - 1}
                      onPress={() => move(column.id, 1)}
                    >
                      <ChevronDown size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onReset}>
            既定に戻す
          </Button>
          <Button color="primary" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
